import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Robot, Trash, EnvelopeOpen, ArrowSquareOut, ArrowsClockwise, CheckCircle, Clock } from "@phosphor-icons/react";

export default function WingmanTriage() {
  const [dashboard, setDashboard] = useState(null);
  const [replies, setReplies] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [cleanStats, setCleanStats] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        api.get("/wingman/gmail-triage/dashboard"),
        api.get("/wingman/gmail-triage/replies-needed"),
      ]);
      setDashboard(d.data);
      setReplies(r.data || []);
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const runNow = async (dryRun) => {
    setRunning(true);
    try {
      const r = await api.post(`/wingman/gmail-triage/run?dry_run=${dryRun}&max_emails=200`);
      const s = r.data.stats;
      toast.success(`Triage : ${s.total_scanned} scannés · ${s.trashed} supprimés · ${s.needs_reply} à répondre`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setRunning(false);
    }
  };

  const markReplied = async (gmailId) => {
    try {
      await api.patch(`/wingman/gmail-triage/mark-replied/${gmailId}`, {});
      reload();
    } catch {
      toast.error("Erreur");
    }
  };

  const cleanPromos = async () => {
    setCleaning(true);
    let totalTrashed = 0, totalUnsub = 0, protectedKept = 0;
    try {
      // Boucle : nettoie par petits lots jusqu'à épuisement (max 15 passes)
      for (let i = 0; i < 15; i++) {
        const r = await api.post(`/gmail/triage/clean-promotions?max_per_run=12&unsubscribe=true`);
        const d = r.data;
        totalTrashed += d.trashed || 0;
        totalUnsub += d.unsubscribed || 0;
        protectedKept = d.kept_protected || protectedKept;
        setCleanStats({ trashed: totalTrashed, unsubscribed: totalUnsub, protectedKept });
        if (d.done || (d.trashed === 0 && d.remaining_estimate === 0)) break;
      }
      toast.success(`Nettoyage terminé : ${totalTrashed} pubs à la corbeille · ${totalUnsub} désinscriptions · ${protectedKept} contacts protégés`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur nettoyage");
    } finally {
      setCleaning(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Chargement…</div>;
  const last = dashboard?.last_run;
  const stats = last?.stats || {};

  return (
    <div className="space-y-6" data-testid="wingman-triage-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black text-3xl">🤖 Wingman Triage Gmail</h1>
          <p className="text-sm text-muted-foreground">
            Tourne automatiquement à <strong>5h00 AM</strong> chaque jour. Trie les promos vers la corbeille, flag les emails qui demandent une réponse.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => runNow(true)} disabled={running} className="hard-border px-3 py-2 hover:bg-muted text-sm" data-testid="dry-run-btn">
            {running ? "..." : "🔍 Test (sans supprimer)"}
          </button>
          <button onClick={() => runNow(false)} disabled={running} className="bg-[#D90429] text-white px-4 py-2 ticker hover:bg-[#BF0424] flex items-center gap-2" data-testid="run-now-btn">
            <Robot size={16} /> {running ? "Triage en cours…" : "Lancer maintenant"}
          </button>
        </div>
      </div>

      {/* LAST RUN STATS */}
      {last ? (
        <div className="hard-border bg-foreground text-white p-5">
          <div className="ticker text-white/60 mb-2">DERNIER PASSAGE · {new Date(last.run_at).toLocaleString("fr-CA")}</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Scannés" value={stats.total_scanned} />
            <Stat label="Promos" value={stats.promo} accent />
            <Stat label="🗑️ Supprimés" value={stats.trashed} accent />
            <Stat label="À répondre" value={stats.needs_reply} accent="amber" />
            <Stat label="Info / Notif" value={(stats.info || 0) + (stats.transactional || 0)} />
          </div>
        </div>
      ) : (
        <div className="hard-border bg-muted/20 p-12 text-center">
          <Robot size={48} className="mx-auto text-muted-foreground mb-3" />
          <div className="font-bold text-lg">Aucun passage encore effectué</div>
          <div className="text-sm text-muted-foreground">Lance le premier triage avec « Test » pour valider sans supprimer, puis « Lancer maintenant » pour vraiment nettoyer.</div>
        </div>
      )}

      {/* NETTOYAGE PUBS EN 1 CLIC */}
      <div className="hard-border bg-white p-5" data-testid="clean-promos-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display font-black text-xl flex items-center gap-2">
              <Trash size={20} className="text-[#D90429]" /> Nettoyer les pubs (désinscription + corbeille)
            </h2>
            <p className="text-sm text-muted-foreground max-w-xl">
              Désinscrit (lien officiel) et envoie à la corbeille toutes les pubs marketing.
              <strong> Contacts protégés</strong> (jamais supprimés) : Hatashita, WAKO, Canadian Muay Thai Open, Fighters Inc, BDC, Le Citoyen, fournisseurs locaux, notifications TikTok. Récupérable 30 jours.
            </p>
          </div>
          <button onClick={cleanPromos} disabled={cleaning}
            className="bg-[#D90429] text-white px-4 py-2 ticker hover:bg-[#BF0424] flex items-center gap-2 disabled:opacity-60"
            data-testid="clean-promos-btn">
            <Trash size={16} /> {cleaning ? "Nettoyage en cours…" : "🧹 Nettoyer maintenant"}
          </button>
        </div>
        {cleanStats && (
          <div className="grid grid-cols-3 gap-3 mt-4" data-testid="clean-promos-stats">
            <Stat label="🗑️ À la corbeille" value={cleanStats.trashed} accent />
            <Stat label="🚫 Désinscriptions" value={cleanStats.unsubscribed} accent="amber" />
            <Stat label="🛡️ Protégés" value={cleanStats.protectedKept} />
          </div>
        )}
      </div>

      {/* REPLIES NEEDED */}
      <div>
        <h2 className="font-display font-black text-xl mb-3">📬 Emails à répondre ({dashboard?.pending_reply || 0})</h2>
        {replies.length === 0 ? (
          <div className="hard-border bg-green-50 p-8 text-center">
            <CheckCircle size={32} weight="fill" className="text-[#0A8754] mx-auto mb-2" />
            <div className="font-bold">Boîte propre !</div>
            <div className="text-sm text-muted-foreground">Aucun email en attente de réponse.</div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="replies-list">
            {replies.map((r, i) => (
              <div key={`${r.gmail_id || "r"}-${i}`} className="hard-border bg-white p-3" data-testid={`reply-${r.gmail_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{r.subject || "(Sans objet)"}</div>
                    <div className="text-xs text-muted-foreground truncate">De : {r.from}</div>
                    <div className="text-xs text-muted-foreground mt-1">{r.snippet}</div>
                    <div className="text-xs text-amber-700 mt-1">📍 Raison : {r.reason}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <a href={`https://mail.google.com/mail/u/impactkb.bjj@gmail.com/#inbox/${r.gmail_id}`} target="_blank" rel="noreferrer" className="hard-border px-2 py-1 text-xs hover:bg-muted flex items-center gap-1" data-testid={`open-${r.gmail_id}`}>
                      <ArrowSquareOut size={12} /> Ouvrir
                    </a>
                    <button onClick={() => markReplied(r.gmail_id)} className="bg-[#0A8754] text-white px-2 py-1 text-xs hover:bg-[#076640]" data-testid={`replied-${r.gmail_id}`}>
                      ✓ Répondu
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* HISTORY */}
      {dashboard?.history?.length > 1 && (
        <div>
          <h2 className="font-display font-black text-xl mb-2">Historique 7 derniers jours</h2>
          <div className="hard-border bg-white">
            <table className="w-full text-sm">
              <thead className="hard-border-b bg-muted/30 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Scannés</th>
                  <th className="px-3 py-2 text-right">Trash</th>
                  <th className="px-3 py-2 text-right">À répondre</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.history.map((h, i) => (
                  <tr key={i} className="hard-border-b">
                    <td className="px-3 py-2">{new Date(h.run_at).toLocaleString("fr-CA")}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.stats?.total_scanned || 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#D90429]">{h.stats?.trashed || 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">{h.stats?.needs_reply || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls = accent === "amber" ? "text-amber-300" : accent ? "text-[#FCA5A5]" : "text-white";
  return (
    <div>
      <div className="ticker text-xs text-white/60">{label}</div>
      <div className={`font-display font-black text-2xl tabular-nums ${cls}`}>{value || 0}</div>
    </div>
  );
}
